import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  ArrowLeft,
  Ticket,
  User,
  Mail,
  Phone,
  Clock,
  Building2,
  Edit,
  MessageSquare,
  FileText,
  Plus,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  Send,
  Lock,
  Loader2,
} from 'lucide-react';
import { cn, formatDate, formatRelativeTime } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/contexts/AuthContext';
import {
  GET_CASE,
  UPDATE_CASE,
  ADD_CASE_ACTIVITY,
  type CaseActivity,
  type CasePriority,
  type CaseStatus,
  type CaseType,
  type GetCaseData,
  type UpdateCaseData,
  type AddCaseActivityData,
} from '@/graphql/cases';

const PRIORITY_BADGES: Record<CasePriority, { label: string; className: string; icon: typeof AlertCircle }> = {
  low: { label: 'Low', className: 'badge-neutral', icon: Clock },
  medium: { label: 'Medium', className: 'badge-primary', icon: Clock },
  high: { label: 'High', className: 'badge-warning', icon: AlertCircle },
  urgent: { label: 'Urgent', className: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon', icon: AlertCircle },
};

const CASE_TYPE_BADGES: Record<CaseType, { label: string; className: string }> = {
  software: { label: 'Software', className: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  services: { label: 'Services', className: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua' },
};

const STATUS_BADGES: Record<CaseStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  open: { label: 'Open', className: 'badge-primary' },
  pending: { label: 'Pending', className: 'badge-warning' },
  resolved: { label: 'Resolved', className: 'badge-secondary' },
  closed: { label: 'Closed', className: 'badge-neutral' },
};

const STATUS_OPTIONS: CaseStatus[] = ['new', 'open', 'pending', 'resolved', 'closed'];

const ACTIVITY_ICONS = {
  reply: MessageSquare,
  internal_note: Lock,
  status_change: CheckCircle2,
  assignment: User,
};

export function CaseDetail() {
  const { caseId } = useParams();
  const { canWrite } = useAuth();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showActivityMenu, setShowActivityMenu] = useState(false);
  const [activeModal, setActiveModal] = useState<'reply' | 'internal_note' | 'edit' | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [editSubject, setEditSubject] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCaseType, setEditCaseType] = useState<CaseType>('software');
  const [editPriority, setEditPriority] = useState<CasePriority>('medium');
  const [editBlastAppNumber, setEditBlastAppNumber] = useState('');

  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const activityMenuRef = useRef<HTMLDivElement>(null);

  // Query case
  const { data, loading, error, refetch } = useQuery<GetCaseData>(GET_CASE, {
    variables: { id: caseId },
    skip: !caseId,
  });

  // Mutations
  const [updateCase] = useMutation<UpdateCaseData>(UPDATE_CASE);
  const [addCaseActivity] = useMutation<AddCaseActivityData>(ADD_CASE_ACTIVITY);

  const caseData = data?.case;
  const activities = caseData?.activities || [];

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (activityMenuRef.current && !activityMenuRef.current.contains(event.target as Node)) {
        setShowActivityMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-pond" />
        <p className="text-chalkboard dark:text-rain-cloud">Loading case...</p>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="p-8 text-center text-rust">
        <AlertCircle className="w-12 h-12 mx-auto mb-3" />
        <p>Error loading case: {error?.message || 'Case not found'}</p>
        <Link to="/cases" className="btn-secondary mt-4 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Cases
        </Link>
      </div>
    );
  }

  const priorityBadge = PRIORITY_BADGES[caseData.priority];
  const statusBadge = STATUS_BADGES[caseData.status];
  const PriorityIcon = priorityBadge.icon;

  const handleStatusChange = async (newStatus: CaseStatus) => {
    setShowStatusDropdown(false);
    try {
      await updateCase({
        variables: { id: caseData.id, input: { status: newStatus } },
      });
      refetch();
    } catch (err) {
      console.error('Status change failed:', err);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) return;
    setIsSaving(true);
    try {
      await addCaseActivity({
        variables: {
          input: { caseId: caseData.id, type: 'reply', content: replyContent },
        },
      });
      setReplyContent('');
      setActiveModal(null);
      refetch();
    } catch (err) {
      console.error('Reply failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitNote = async () => {
    if (!noteContent.trim()) return;
    setIsSaving(true);
    try {
      await addCaseActivity({
        variables: {
          input: { caseId: caseData.id, type: 'internal_note', content: noteContent },
        },
      });
      setNoteContent('');
      setActiveModal(null);
      refetch();
    } catch (err) {
      console.error('Note failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditSubject(caseData.subject);
    setEditDescription(caseData.description || '');
    setEditCaseType(caseData.caseType);
    setEditPriority(caseData.priority);
    setEditBlastAppNumber(caseData.blastAppNumber || '');
    setActiveModal('edit');
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await updateCase({
        variables: {
          id: caseData.id,
          input: {
            subject: editSubject,
            description: editDescription || undefined,
            caseType: editCaseType,
            priority: editPriority,
            blastAppNumber: editBlastAppNumber || undefined,
          },
        },
      });
      setActiveModal(null);
      refetch();
    } catch (err) {
      console.error('Update failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderActivityContent = (activity: CaseActivity) => {
    switch (activity.type) {
      case 'status_change':
        return (
          <span className="text-sm text-chalkboard dark:text-rain-cloud">
            Changed status from{' '}
            <span className={cn('badge badge-sm', STATUS_BADGES[activity.oldStatus || 'new'].className)}>
              {STATUS_BADGES[activity.oldStatus || 'new'].label}
            </span>
            {' '}to{' '}
            <span className={cn('badge badge-sm', STATUS_BADGES[activity.newStatus || 'open'].className)}>
              {STATUS_BADGES[activity.newStatus || 'open'].label}
            </span>
          </span>
        );
      case 'assignment':
        return (
          <span className="text-sm text-chalkboard dark:text-rain-cloud">
            {activity.oldAssigneeName
              ? `Reassigned from ${activity.oldAssigneeName} to ${activity.newAssigneeName || 'Unassigned'}`
              : `Assigned to ${activity.newAssigneeName}`}
          </span>
        );
      default:
        return (
          <p className="text-sm text-slate dark:text-mist whitespace-pre-wrap">
            {activity.content}
          </p>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/cases"
        className="inline-flex items-center gap-2 text-sm text-chalkboard dark:text-rain-cloud hover:text-pond dark:hover:text-pond-light transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Cases
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
            <Ticket className="w-7 h-7 text-pond dark:text-pond-light" />
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-pond dark:text-pond-light">
                {caseData.ticketNumber}
              </span>
              <span className={cn('badge', CASE_TYPE_BADGES[caseData.caseType].className)}>
                {CASE_TYPE_BADGES[caseData.caseType].label}
              </span>
              <span className={cn('badge inline-flex items-center gap-1', priorityBadge.className)}>
                <PriorityIcon className="w-3 h-3" />
                {priorityBadge.label}
              </span>
              <div className="relative" ref={statusDropdownRef}>
                <button
                  onClick={() => canWrite('cases') && setShowStatusDropdown(!showStatusDropdown)}
                  disabled={!canWrite('cases')}
                  className={cn(
                    'badge inline-flex items-center gap-1 transition-opacity',
                    statusBadge.className,
                    canWrite('cases') && 'cursor-pointer hover:opacity-80'
                  )}
                >
                  {statusBadge.label}
                  {canWrite('cases') && <ChevronDown className="w-3 h-3" />}
                </button>
                {showStatusDropdown && (
                  <div className="absolute left-0 top-full mt-1 w-36 bg-white dark:bg-navy rounded-lg shadow-lg border border-gray dark:border-night-pond py-1 z-10">
                    {STATUS_OPTIONS.map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-pale-gray-2 dark:hover:bg-night-pond transition-colors',
                          status === caseData.status
                            ? 'text-pond dark:text-pond-light font-medium'
                            : 'text-slate dark:text-mist'
                        )}
                      >
                        <span className={cn('w-2 h-2 rounded-full', STATUS_BADGES[status].className)} />
                        {STATUS_BADGES[status].label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-slate dark:text-mist mt-2">
              {caseData.subject}
            </h1>
            <p className="text-chalkboard dark:text-rain-cloud mt-1">
              Opened {formatRelativeTime(new Date(caseData.createdAt))} • Updated {formatRelativeTime(new Date(caseData.updatedAt))}
            </p>
          </div>
        </div>

        {canWrite('cases') && (
          <button onClick={handleOpenEditModal} className="btn-secondary">
            <Edit className="w-4 h-4" />
            Edit
          </button>
        )}
      </div>

      {/* Case Details */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-slate dark:text-mist">Case Details</h2>
        </div>
        <div className="card-body space-y-4">
          {/* Info Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-gray dark:border-night-pond">
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Assigned To</p>
              {caseData.assignedToName ? (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-6 h-6 rounded-full bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                    <User className="w-3 h-3 text-pond dark:text-pond-light" />
                  </div>
                  <span className="text-slate dark:text-mist">{caseData.assignedToName}</span>
                </div>
              ) : (
                <span className="text-chalkboard dark:text-rain-cloud italic mt-1 block">Unassigned</span>
              )}
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Priority</p>
              <span className={cn('badge mt-1 inline-flex items-center gap-1', priorityBadge.className)}>
                <PriorityIcon className="w-3 h-3" />
                {priorityBadge.label}
              </span>
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Created</p>
              <p className="text-slate dark:text-mist mt-1">{formatDate(new Date(caseData.createdAt))}</p>
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Last Updated</p>
              <p className="text-slate dark:text-mist mt-1">{formatRelativeTime(new Date(caseData.updatedAt))}</p>
            </div>
          </div>

          {/* Client & Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 pb-4 border-b border-gray dark:border-night-pond">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                <Building2 className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Client</p>
                <Link
                  to={`/clients/${caseData.organizationId}`}
                  className="font-medium text-pond dark:text-pond-light hover:underline"
                >
                  {caseData.organization?.name || 'Unknown'}
                </Link>
              </div>
            </div>

            {caseData.contact && (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                    <User className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
                  </div>
                  <div>
                    <p className="text-sm text-chalkboard dark:text-rain-cloud">Contact</p>
                    <p className="font-medium text-slate dark:text-mist">
                      {caseData.contact.firstName} {caseData.contact.lastName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                    <Mail className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
                  </div>
                  <div>
                    <p className="text-sm text-chalkboard dark:text-rain-cloud">Email</p>
                    <a
                      href={`mailto:${caseData.contact.email}`}
                      className="font-medium text-pond dark:text-pond-light hover:underline"
                    >
                      {caseData.contact.email}
                    </a>
                  </div>
                </div>

                {caseData.contact.phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                      <Phone className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
                    </div>
                    <div>
                      <p className="text-sm text-chalkboard dark:text-rain-cloud">Phone</p>
                      <a
                        href={`tel:${caseData.contact.phone}`}
                        className="font-medium text-pond dark:text-pond-light hover:underline"
                      >
                        {caseData.contact.phone}
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* BLAST Application Link */}
          {caseData.blastAppNumber && (
            <div className="pt-4 pb-4 border-b border-gray dark:border-night-pond">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-violet dark:text-violet-light" />
                </div>
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">BLAST Application</p>
                  <span className="font-medium text-violet dark:text-violet-light">
                    {caseData.blastAppNumber}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {caseData.description && (
            <div className="pt-4">
              <p className="text-sm text-chalkboard dark:text-rain-cloud mb-2">Description</p>
              <p className="text-slate dark:text-mist whitespace-pre-wrap">{caseData.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="font-semibold text-slate dark:text-mist">Activity</h2>
          {canWrite('cases') && (
            <div className="relative" ref={activityMenuRef}>
              <button
                onClick={() => setShowActivityMenu(!showActivityMenu)}
                className="btn-secondary btn-sm"
              >
                <Plus className="w-4 h-4" />
                Add
                <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {showActivityMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-navy rounded-lg shadow-lg border border-gray dark:border-night-pond py-1 z-10">
                  <button
                    onClick={() => {
                      setShowActivityMenu(false);
                      setActiveModal('reply');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate dark:text-mist hover:bg-pale-gray-2 dark:hover:bg-night-pond transition-colors"
                  >
                    <Send className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                    Reply to Customer
                  </button>
                  <button
                    onClick={() => {
                      setShowActivityMenu(false);
                      setActiveModal('internal_note');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate dark:text-mist hover:bg-pale-gray-2 dark:hover:bg-night-pond transition-colors"
                  >
                    <Lock className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                    Internal Note
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="card-body">
          {activities.length === 0 ? (
            <p className="text-center text-chalkboard dark:text-rain-cloud py-4">
              No activity yet. Click Add to add a reply or note.
            </p>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => {
                const ActivityIcon = ACTIVITY_ICONS[activity.type] || FileText;
                const isLast = index === activities.length - 1;
                const isInternalNote = activity.type === 'internal_note';

                return (
                  <div key={activity.id} className="flex gap-3">
                    <div className="relative">
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center',
                          isInternalNote
                            ? 'bg-amber-100 dark:bg-amber-900/30'
                            : 'bg-pond/10 dark:bg-pond-light/20'
                        )}
                      >
                        <ActivityIcon
                          className={cn(
                            'w-4 h-4',
                            isInternalNote
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-pond dark:text-pond-light'
                          )}
                        />
                      </div>
                      {!isLast && (
                        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray dark:bg-night-pond" />
                      )}
                    </div>
                    <div className={cn('flex-1 pb-4', isInternalNote && 'bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 -ml-1')}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate dark:text-mist">
                          {activity.userName}
                        </span>
                        {isInternalNote && (
                          <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">
                            Internal
                          </span>
                        )}
                        <span className="text-xs text-chalkboard dark:text-rain-cloud">
                          • {formatRelativeTime(new Date(activity.createdAt))}
                        </span>
                      </div>
                      <div className="mt-1">
                        {renderActivityContent(activity)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Reply Modal */}
      <Modal
        isOpen={activeModal === 'reply'}
        onClose={() => setActiveModal(null)}
        title="Reply to Customer"
      >
        <div className="space-y-4">
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            This reply will be visible to the customer.
          </p>
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Type your reply..."
            rows={6}
            className="input resize-none"
            autoFocus
            disabled={isSaving}
          />
          <div className="flex justify-end gap-3">
            <button onClick={() => setActiveModal(null)} className="btn-secondary" disabled={isSaving}>
              Cancel
            </button>
            <button onClick={handleSubmitReply} disabled={!replyContent.trim() || isSaving} className="btn-primary">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Reply
            </button>
          </div>
        </div>
      </Modal>

      {/* Internal Note Modal */}
      <Modal
        isOpen={activeModal === 'internal_note'}
        onClose={() => setActiveModal(null)}
        title="Internal Note"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              This note is internal and will NOT be visible to the customer.
            </p>
          </div>
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Add internal notes..."
            rows={6}
            className="input resize-none"
            autoFocus
            disabled={isSaving}
          />
          <div className="flex justify-end gap-3">
            <button onClick={() => setActiveModal(null)} className="btn-secondary" disabled={isSaving}>
              Cancel
            </button>
            <button onClick={handleSubmitNote} disabled={!noteContent.trim() || isSaving} className="btn-primary">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Add Note
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={activeModal === 'edit'}
        onClose={() => setActiveModal(null)}
        title="Edit Case"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">Subject</label>
            <input
              type="text"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
              className="input"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">Description</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={5}
              className="input resize-none"
              disabled={isSaving}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">Type</label>
              <select
                value={editCaseType}
                onChange={(e) => setEditCaseType(e.target.value as CaseType)}
                className="select"
                disabled={isSaving}
              >
                <option value="software">Software</option>
                <option value="services">Services</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">Priority</label>
              <select
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value as CasePriority)}
                className="select"
                disabled={isSaving}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              BLAST Application Number
            </label>
            <input
              type="text"
              value={editBlastAppNumber}
              onChange={(e) => setEditBlastAppNumber(e.target.value)}
              placeholder="e.g., AP-2025-00123 (optional)"
              className="input"
              disabled={isSaving}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setActiveModal(null)} className="btn-secondary" disabled={isSaving}>
              Cancel
            </button>
            <button onClick={handleSaveEdit} disabled={!editSubject.trim() || isSaving} className="btn-primary">
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
